/**
 * Users Page Component
 * 
 * User management interface for admins
 */

import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import { userAPI, clinicAPI } from '../services/api'
import { Button } from '../components/ui/button'
import { Card, CardContent } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Badge } from '../components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import UserModal from '../components/UserModal'
import UserAnalyticsView from '../components/UserAnalyticsView'
import { ROLE_LABELS } from '../lib/roles'


const ROLE_COLORS = {
  super_admin: 'bg-purple-100 text-purple-800',
  clinic_admin: 'bg-blue-100 text-blue-800',
  physician_manager: 'bg-green-100 text-green-800',
  pharmacy_manager: 'bg-yellow-100 text-yellow-800',
  program_manager: 'bg-indigo-100 text-indigo-800',
  data_entry: 'bg-gray-100 text-gray-800',
  viewer: 'bg-slate-100 text-slate-800',
  report_viewer: 'bg-cyan-100 text-cyan-800'
}

// The gate is its own component so the page's hooks always run in the same
// order: hooks after an early return crash React when the condition flips.
export default function UsersPage() {
  const { user: currentUser } = useAuthStore()
  if (!['super_admin', 'clinic_admin'].includes(currentUser?.role)) {
    return (
      <div className="text-center py-12">
        <svg className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
        </svg>
        <h3 className="text-lg font-medium text-foreground mb-2">Access Denied</h3>
        <p className="text-muted-foreground">You don't have permission to manage users.</p>
      </div>
    )
  }
  return <UsersPageContent />
}

function UsersPageContent() {
  const { user: currentUser } = useAuthStore()
  
  // State
  const [activeTab, setActiveTab] = useState('users')
  const [users, setUsers] = useState([])
  const [clinics, setClinics] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('true')
  const [clinicFilter, setClinicFilter] = useState('')
  const [page, setPage] = useState(1)
  const [totalUsers, setTotalUsers] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [userModal, setUserModal] = useState({ isOpen: false, user: null })
  
  const limit = 20

  
  // Fetch users
  useEffect(() => {
    fetchUsers()
  }, [page, roleFilter, statusFilter, clinicFilter, searchQuery])
  
  // Fetch clinics (for filter)
  useEffect(() => {
    fetchClinics()
  }, [])
  
  const fetchUsers = async () => {
    setLoading(true)
    try {
      const params = {
        page,
        limit,
        isActive: statusFilter
      }
      
      if (roleFilter) params.role = roleFilter
      if (clinicFilter) params.clinicId = clinicFilter
      if (searchQuery) params.search = searchQuery
      
      const response = await userAPI.list(params)
      
      if (response.data.success) {
        setUsers(response.data.data || [])
        setTotalUsers(response.data.pagination?.total || 0)
        setTotalPages(response.data.pagination?.pages || 1)
      }
    } catch (error) {
      console.error('Failed to fetch users:', error)
      setUsers([])
    } finally {
      setLoading(false)
    }
  }
  
  const fetchClinics = async () => {
    try {
      const response = await clinicAPI.list()
      if (response.data.success) {
        setClinics(response.data.data || [])
      }
    } catch (error) {
      console.error('Failed to fetch clinics:', error)
      setClinics([])
    }
  }
  
  const handleCreateUser = () => {
    setUserModal({ isOpen: true, user: null })
  }
  
  const handleEditUser = (user) => {
    setUserModal({ isOpen: true, user })
  }
  
  const handleCloseModal = () => {
    setUserModal({ isOpen: false, user: null })
  }
  
  const handleSaveUser = () => {
    fetchUsers() // Refresh list
  }
  
  const handleToggleStatus = async (user) => {
    try {
      await userAPI.update(user.id, { isActive: !user.isActive })
      fetchUsers()
    } catch (error) {
      console.error('Failed to toggle user status:', error)
      alert('Failed to update user status')
    }
  }
  
  const getClinicNames = (clinicIds) => {
    if (!clinicIds || clinicIds.length === 0) return 'No clinics'
    if (clinicIds.length === 1) {
      const clinic = clinics.find(c => c.clinicId === clinicIds[0])
      return clinic?.name || clinicIds[0]
    }
    return `${clinicIds.length} clinics`
  }
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)
    
    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    
    return date.toLocaleDateString()
  }
  
  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase()
  }

  // A clinic admin can invite a peer admin but cannot manage one afterwards —
  // editing, deactivating, and password resets on an admin account are all
  // super admin actions. Mirrors denyAdminModification in
  // backend/src/routes/users.js; rendering the buttons anyway would only turn
  // into a 403 on save.
  const canManage = (target) => {
    if (currentUser?.role === 'super_admin') return true
    if (target.id === currentUser?.id) return true
    return !['super_admin', 'clinic_admin'].includes(target.role)
  }


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">User Management</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage user accounts and view analytics
          </p>
        </div>
        {activeTab === 'users' && ['super_admin', 'clinic_admin'].includes(currentUser?.role) && (
          <Button onClick={handleCreateUser}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add User
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-6 mt-6">
          {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-64">
              <Input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setPage(1) // Reset to first page on search
                }}
              />
            </div>
            <div className="min-w-48">
              <Select value={roleFilter} onValueChange={(value) => {
                setRoleFilter(value === 'all' ? '' : value)
                setPage(1)
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {currentUser?.role === 'super_admin' && (
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  )}
                  <SelectItem value="clinic_admin">Clinic Admin</SelectItem>
                  <SelectItem value="physician_manager">Physician Manager</SelectItem>
                  <SelectItem value="pharmacy_manager">Pharmacy Manager</SelectItem>
                  <SelectItem value="program_manager">Program Manager</SelectItem>
                  <SelectItem value="data_entry">Data Entry</SelectItem>
                  <SelectItem value="viewer">View All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-48">
              <Select value={statusFilter} onValueChange={(value) => {
                setStatusFilter(value)
                setPage(1)
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="true">Active</SelectItem>
                  <SelectItem value="false">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {currentUser?.role === 'super_admin' && clinics.length > 0 && (
              <div className="min-w-48">
                <Select value={clinicFilter} onValueChange={(value) => {
                  setClinicFilter(value === 'all' ? '' : value)
                  setPage(1)
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Clinics" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clinics</SelectItem>
                    {clinics.map((clinic) => (
                      <SelectItem key={clinic.clinicId} value={clinic.clinicId}>
                        {clinic.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-4 font-medium text-muted-foreground">User</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Role</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Clinics</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Status</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Last Login</th>
                  <th className="text-left p-4 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="ml-3 text-muted-foreground">Loading users...</span>
                      </div>
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-muted-foreground">
                      <svg className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                      </svg>
                      <p>No users found</p>
                      <p className="text-sm">Try adjusting your filters or search query</p>
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                      <td className="p-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              user.role === 'super_admin' 
                                ? 'bg-purple-100' 
                                : 'bg-primary/10'
                            }`}>
                              <span className={`text-sm font-medium ${
                                user.role === 'super_admin' 
                                  ? 'text-purple-600' 
                                  : 'text-primary'
                              }`}>
                                {getInitials(user.firstName, user.lastName)}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-foreground">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-sm text-muted-foreground">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge className={ROLE_COLORS[user.role] || 'bg-gray-100 text-gray-800'}>
                          {ROLE_LABELS[user.role] || user.role}
                        </Badge>
                        {user.role === 'report_viewer' && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {Array.isArray(user.permissions?.reportPages)
                              ? `${user.permissions.reportPages.length} selected pages`
                              : 'All role pages'}
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-muted-foreground">
                          {user.role === 'super_admin' ? 'All Clinics' : getClinicNames(user.clinicIds)}
                        </span>
                      </td>
                      <td className="p-4">
                        {user.isActive ? (
                          <Badge className="bg-green-100 text-green-800">Active</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800">Inactive</Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-muted-foreground">
                          {formatDate(user.metadata?.lastLogin)}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex space-x-2">
                          {canManage(user) ? (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleEditUser(user)}
                              >
                                Edit
                              </Button>
                              {user.id !== currentUser?.id && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className={user.isActive ? 'text-destructive hover:text-destructive' : 'text-green-600 hover:text-green-700'}
                                  onClick={() => handleToggleStatus(user)}
                                >
                                  {user.isActive ? 'Disable' : 'Enable'}
                                </Button>
                              )}
                            </>
                          ) : (
                            <span className="text-sm text-muted-foreground">Super admin only</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
          {!loading && users.length > 0 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{' '}
                <span className="font-medium">{Math.min(page * limit, totalUsers)}</span> of{' '}
                <span className="font-medium">{totalUsers}</span> results
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          <UserAnalyticsView />
        </TabsContent>
      </Tabs>

      {/* User Modal */}
      <UserModal
        isOpen={userModal.isOpen}
        onClose={handleCloseModal}
        onSave={handleSaveUser}
        user={userModal.user}
      />
    </div>
  )
}
